import { initializeApp, type FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getAuth, GoogleAuthProvider, type Auth } from 'firebase/auth';
import {
  initializeFirestore,
  memoryLocalCache,
  type Firestore,
} from 'firebase/firestore';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

if (!firebaseConfig.apiKey) {
  throw new Error(
    'Firebase config no encontrado. Copia .env.example a .env.local y rellena los valores.',
  );
}

export const firebaseApp: FirebaseApp = initializeApp(firebaseConfig);

// App Check (reCAPTCHA v3) — bloquea scrapers que llaman directamente a
// `firestore.googleapis.com` con la API key extraída del bundle. El
// proyecto `manager-atletismo` tiene App Check en modo Enforce para
// Cloud Firestore, así que cualquier rama desplegada al proyecto debe
// inicializarlo o sus reads serán rechazados con "Missing or
// insufficient permissions". Si la site key no está configurada, no
// inicializamos (no rompe local / previews).
//
// Modo debug en DEV: reCAPTCHA v3 en localhost da scores demasiado
// bajos y App Check rechaza. Con este flag activo, el SDK emite un
// debug token estable que se loguea en consola la primera vez
// ("App Check debug token: <uuid>"). Cada dev tiene que copiar ese
// UUID y registrarlo en Firebase Console → App Check → Apps →
// "Manage debug tokens". Cada navegador (incógnito incluido) necesita
// su propio token. El flag NUNCA se activa en producción
// (import.meta.env.DEV → false → dead code en el build).
if (import.meta.env.DEV) {
  (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
}
const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
if (recaptchaSiteKey) {
  initializeAppCheck(firebaseApp, {
    provider: new ReCaptchaV3Provider(recaptchaSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
} else if (import.meta.env.PROD) {
  console.warn('[firebase] VITE_RECAPTCHA_SITE_KEY no configurada; App Check no activo en producción.');
}

// Caché en memoria (sin IndexedDB). Decisión deliberada para evitar dos
// problemas del modo persistente con uploads grandes:
//  1) `commit()` no resuelve hasta el ack del servidor, así que el flujo de
//     upload puede aplicar contrapresión real y no satura el pipeline interno
//     del SDK con `resource-exhausted`.
//  2) Si el usuario aborta un upload, no quedan mutaciones encoladas en
//     IndexedDB que el SDK intente reenviar al reabrir la app.
// Trade-off: se pierde la caché entre sesiones; queries repetidas dentro de
// la misma sesión siguen siendo rápidas porque el snapshot cache vive en RAM.
export const db: Firestore = initializeFirestore(firebaseApp, {
  localCache: memoryLocalCache(),
  // Algunos parsers (xlsxGroups, xlsxRankings) producen campos opcionales como
  // `undefined`. Sin esto, Firestore rechaza el setDoc entero con
  // "Unsupported field value: undefined" y la escritura se pierde.
  ignoreUndefinedProperties: true,
});

export const auth: Auth = getAuth(firebaseApp);
export const googleProvider = new GoogleAuthProvider();
